import { useMemo } from "react";
import { Box, Text } from "@mantine/core";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { hostOf, isGoogleGoto, isOwnHost } from "./domains";
import { useBrandContext } from "../Brands/brandContext";
import classes from "./AnswerMarkdown.module.css";

/**
 * An AI answer, rendered the way a reader needs to read it.
 *
 * One component for every view that shows one, because they all show the SAME text: the daily
 * detection samples and the weekly monitor both store the answer as the surface returned it, and
 * an answer whose citations are legible in one place and a wall of "[3]" in another is the same
 * answer being explained twice, badly.
 *
 * What it adds over a bare <Markdown>:
 *
 * - Citations become chips reading `[3] tenderstrike.com`, with the full url on hover, and the
 *   brand's own pages are marked out. See markdownComponents.
 * - Named brands can be highlighted in place. See rehypeHighlightTerms.
 * - The answer reads as a quotation inside the page rather than as a second document — headings
 *   that do not outshout their host, tables that scroll instead of widening the page.
 */

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * A rehype plugin that wraps every occurrence of `terms` in a `<mark>`.
 *
 * Written as a plugin rather than applied to the raw string because the answer has to render as
 * markdown — it arrives full of `**bold**`, lists and citation links, and shown as plain text it
 * was unreadable. Splitting the source string on the terms before parsing would corrupt the
 * markdown; splitting the parsed tree afterwards cannot.
 *
 * Best effort, deliberately. The backend matcher normalises German spellings and diacritics before
 * comparing, so it can legitimately match a form this plain regex will not find — which is why the
 * matched terms are ALSO listed as chips above. A missed highlight is cosmetic; the chip list is
 * the authoritative record.
 */
function rehypeHighlightTerms(terms) {
  const usable = (terms ?? []).filter(Boolean).map(escapeRegExp);
  if (usable.length === 0) return () => () => {};

  const pattern = new RegExp(`(${usable.join("|")})`, "gi");

  return () => (tree) => {
    const walk = (node) => {
      if (!node.children) return;
      const next = [];
      for (const child of node.children) {
        // Never highlight inside a link label: the citation markers are already `[1]`, and
        // marking them turns the reference list into a wall of yellow.
        if (child.type === "element" && child.tagName === "a") {
          next.push(child);
          continue;
        }
        if (child.type !== "text") {
          walk(child);
          next.push(child);
          continue;
        }
        // String.split with ONE capture group puts the captures at odd indices.
        const parts = child.value.split(pattern);
        if (parts.length === 1) {
          next.push(child);
          continue;
        }
        parts.forEach((part, i) => {
          if (part === "") return;
          next.push(
            i % 2 === 1
              ? {
                  type: "element",
                  tagName: "mark",
                  properties: {},
                  children: [{ type: "text", value: part }],
                }
              : { type: "text", value: part },
          );
        });
      }
      node.children = next;
    };
    walk(tree);
  };
}

/**
 * A citation marker — the `[1]` label Google puts on an inline source link.
 *
 * Anything else keeps its own words: an answer that writes `[TenderStrike](…)` is naming the
 * brand in prose, and turning that into a chip would hide the sentence it was part of.
 */
const CITATION_MARKER = /^\[?\d{1,3}\]?$/;

/**
 * The site a citation points at, or null when it cannot be known.
 *
 * Google wraps some AI Mode citations in `google.com/goto?url=<blob>`, where the blob is 90 bytes
 * of signed payload with no readable url inside. Those cannot be named here — the resolved domain
 * exists only on the sample, and nothing maps a marker back to it — so they stay plain markers
 * rather than a page full of chips all reading "google.com".
 */
const citationHost = (href) => {
  if (isGoogleGoto(href)) return null;
  return hostOf(href);
};

/**
 * How the answer's own markup renders.
 *
 * The citation links are the reason this exists. A bare `[3]` says an assertion was sourced and
 * refuses to say from where, so the reader cannot tell the answer that read their site from the
 * one that read a competitor's — which is the single most useful thing an answer can tell them.
 * Rendered as `[3] tenderstrike.com`, with the full url on hover, the sourcing is legible at a
 * glance and the brand's own pages stand out.
 *
 * A factory rather than a constant, because the chips have to know which host is the brand's own.
 */
const markdownComponents = (ownDomain) => ({
  a: ({ children, href, ...props }) => {
    // An empty label is a citation anchor with nothing to show — Google emits one per element,
    // duplicating the first numbered marker. Rendering it leaves an invisible clickable sliver.
    const label = Array.isArray(children) ? children.join("") : children;
    const text = label == null ? "" : String(label).trim();
    if (text === "") return null;

    const host = CITATION_MARKER.test(text) ? citationHost(href) : null;
    if (!host) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    }

    const own = isOwnHost(ownDomain, host);
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        // The native title, not a Mantine Tooltip: one answer carries thirty of these.
        title={href}
        className={own ? `${classes.citation} ${classes.citationOwn}` : classes.citation}
      >
        <span className={classes.citationMarker}>[{text.replace(/^\[|\]$/g, "")}]</span>
        {host}
      </a>
    );
  },
  // The answer is a fragment inside a card, not a document: its headings must not outshout the
  // page's own.
  h1: ({ children }) => <Text fw={700} fz="sm" mt="sm">{children}</Text>,
  h2: ({ children }) => <Text fw={700} fz="sm" mt="sm">{children}</Text>,
  h3: ({ children }) => <Text fw={600} fz="sm" mt="sm">{children}</Text>,
  table: ({ children }) => (
    <Box style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: "inherit" }}>{children}</table>
    </Box>
  ),
});

/**
 * @param text            The answer, as markdown. Renders nothing when absent.
 * @param ownDomain       The brand's own bare host. Defaults to the selected brand's website,
 *                        which is right for every view that shows one brand at a time.
 * @param highlightTerms  Names to mark in the text — matched spellings, extracted brands.
 * @param maxHeight       Caps the height and scrolls inside. Omit to let the answer run.
 */
export default function AnswerMarkdown({ text, ownDomain, highlightTerms, maxHeight }) {
  const selectedBrand = useBrandContext((s) => s.selectedBrand);
  const domain = ownDomain ?? hostOf(selectedBrand?.websiteUrl);

  // Memoised on the domain alone: the components object is a dependency of every node react
  // markdown renders, and a fresh one per render re-renders the whole answer.
  const components = useMemo(() => markdownComponents(domain), [domain]);

  if (!text) return null;

  return (
    <Box
      className={classes.answer}
      style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlightTerms(highlightTerms)]}
        components={components}
      >
        {text}
      </Markdown>
    </Box>
  );
}
